import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Download,
  FolderKanban,
  Loader2,
  PanelRight,
  PanelRightClose,
  Search,
  Settings,
  Share2,
  Trash2,
  UploadCloud,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { APPROVAL_STATUS_LABELS } from "@/lib/api-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useGuestWatchData,
  useIdentifyGuest,
  useWorkspaceWatchData,
  useAutoRegisterView,
  useWatchComments,
  type WatchVersion,
  type WatchComment,
} from "@/hooks/use-watch";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { useProjects } from "@/hooks/use-projects";
import { roleDisplayName } from "@/lib/api-types";
import { type VideoPlayerHandle } from "@/components/watch/video-player";
import { MediaSurface } from "@/components/watch/media-surface";
import { RightPanel } from "@/components/watch/right-panel";
import { AnnotationProvider } from "@/components/watch/annotation-context";
import { ShareDialog } from "@/components/shares/share-dialog";
import { SidebarRail, type RailNavItem } from "@/components/layout/sidebar-rail";
import { NotificationBell } from "@/components/layout/notification-bell";
import { UserMenu } from "@/components/layout/user-menu";

/**
 * Watch page (Task 9 core + Tasks 5-17 buildout).
 *
 * ONE component, TWO entry points. Mode is derived from the route:
 *   - `/watch/:token`           → guest mode  (share-link reviewer, no rail)
 *   - `/app/watch/:videoId`     → workspace mode (rail + workspace top bar)
 *   - `/admin/watch/:videoId`   → workspace mode with admin nav in the rail
 *
 * Task 17 lifted the watch routes OUT of AppLayout/AdminLayout to fix the
 * double-top-bar + scroll bug (video wasn't viewport-locked before). Watch page
 * now owns the full viewport (`h-screen`) and renders its own rail + top bar.
 */
export default function WatchPage() {
  const location = useLocation();
  const isGuest = location.pathname.startsWith("/watch/");
  return (
    <AnnotationProvider>
      {isGuest ? <GuestWatch /> : <WorkspaceWatch />}
    </AnnotationProvider>
  );
}

// ── Workspace mode ───────────────────────────────────────────────────────────

function WorkspaceWatch() {
  const { videoId } = useParams<{ videoId: string }>();
  const { data, isLoading, isError, error } = useWorkspaceWatchData(videoId);
  // Fallback target only — the Back button does history-back (navigate(-1))
  // and uses this href when there's no in-app history (direct load / refresh).
  const backHref = useWorkspaceBackHref(data?.projectId ?? null);

  // Register the view once the stack root is known.
  useAutoRegisterView("workspace", data?.rootVideoId, !!data?.rootVideoId);

  // Comments are PER VERSION (Frame.io parity). WatchShell fetches them for the
  // active version so switching versions swaps the thread — nothing to fetch here.

  if (isLoading) return <WatchLoading />;
  if (isError || !data) return <WatchError message={(error as Error)?.message ?? "Unable to load video"} backHref={backHref} />;

  const latest = pickLatest(data.versions);
  const rootVersion = data.versions.find((v) => v.id === data.rootVideoId);
  const rootShareToken = rootVersion?.shareToken ?? null;
  const rootDownloadAllowed = !!rootVersion?.downloadAllowed;
  return (
    <WatchShell
      mode="workspace"
      backHref={backHref}
      title={latest?.title || "Untitled"}
      currentVersion={latest}
      versions={data.versions}
      comments={[]}
      watchKey={videoId}
      rootVideoId={data.rootVideoId}
      rootShareToken={rootShareToken}
      rootDownloadAllowed={rootDownloadAllowed}
    />
  );
}

/** Route-aware back path — /app/... → /app/projects, /admin/... → /admin/projects. */
function useWorkspaceBackHref(projectId: string | null) {
  const { pathname } = useLocation();
  const base = pathname.startsWith("/admin/") ? "/admin/projects" : "/app/projects";
  // On direct load / refresh (no in-app history), land on the video's own
  // project instead of the bare projects list. Falls back to the list when the
  // project id isn't known yet.
  return projectId ? `${base}/${projectId}` : base;
}

// ── Guest mode ───────────────────────────────────────────────────────────────

function GuestWatch() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, isError, error, refetch } = useGuestWatchData(token);

  // Only auto-register a view after identify succeeds (skipForm=true).
  useAutoRegisterView("guest", token, !!data?.skipForm);

  // Comments fetch is gated on skipForm — no point before identify.
  const { data: comments = [] } = useWatchComments("guest", data?.skipForm ? token : undefined);

  if (isLoading) return <WatchLoading />;
  if (isError || !data) {
    return (
      <WatchError message={(error as Error)?.message ?? "This link is invalid or expired"} backHref={null} />
    );
  }

  if (!data.skipForm) {
    return <GuestIdentifyForm token={token!} onIdentified={() => refetch()} adminName={data.adminName} />;
  }

  return (
    <WatchShell
      mode="guest"
      backHref={null}
      title={data.video.title}
      currentVersion={{
        id: data.video.id,
        title: data.video.title,
        versionNumber: data.video.versionNumber,
        assetType: data.video.assetType,
        approvalStatus: data.video.approvalStatus,
        cdnUrl: data.video.cdnUrl,
      }}
      versions={data.versions}
      adminName={data.adminName}
      comments={comments}
      watchKey={token}
      shareToken={token}
      downloadAllowed={data.video.downloadAllowed}
    />
  );
}

function GuestIdentifyForm({
  token,
  onIdentified,
  adminName,
}: {
  token: string;
  onIdentified: () => void;
  adminName?: string;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const identify = useIdentifyGuest(token);

  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <form
        className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-6"
        onSubmit={(e) => {
          e.preventDefault();
          identify.mutate(
            { name: name.trim(), email: email.trim() },
            { onSuccess: () => onIdentified() },
          );
        }}
      >
        <div className="space-y-1">
          <div className="text-lg font-semibold text-foreground">
            {adminName ? `${adminName} shared a video with you` : "Review this video"}
          </div>
          <div className="text-sm text-muted-foreground">
            Enter your name and email to start reviewing.
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="guest-name">Name</Label>
          <Input
            id="guest-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            placeholder="Full name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="guest-email">Email</Label>
          <Input
            id="guest-email"
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            placeholder="you@example.com"
          />
        </div>
        {identify.error && (
          <p className="text-sm text-destructive">
            {(identify.error as Error).message || "Something went wrong. Try again."}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={identify.isPending || !name.trim() || !email.trim()}>
          {identify.isPending ? "Loading..." : "Continue"}
        </Button>
      </form>
    </div>
  );
}

// ── Shared shell ─────────────────────────────────────────────────────────────

type ShellProps = {
  mode: "workspace" | "guest";
  backHref: string | null;
  title: string;
  currentVersion: WatchVersion | null;
  versions: WatchVersion[];
  adminName?: string;
  comments?: WatchComment[];
  /** Fed to comment mutation hooks for query invalidation. */
  watchKey?: string;
  /** Guest mode only: the share token in the URL, needed for /download. */
  shareToken?: string;
  /** Guest mode only: whether the root video's share allows download. */
  downloadAllowed?: boolean;
  /** Workspace mode only: stack root id — Share button targets root, not viewed version. */
  rootVideoId?: string;
  /** Workspace mode only: existing share token if any, so Share can copy without POSTing. */
  rootShareToken?: string | null;
  /** Workspace mode only: current downloadAllowed value on the root. */
  rootDownloadAllowed?: boolean;
};

function WatchShell({
  mode,
  backHref,
  title,
  currentVersion,
  versions,
  adminName,
  comments = [],
  watchKey,
  shareToken,
  downloadAllowed,
  rootVideoId,
  rootShareToken,
  rootDownloadAllowed,
}: ShellProps) {
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const playerRef = useRef<VideoPlayerHandle>(null);
  // Seeking to a comment also pauses playback — matches Frame.io behavior
  // where you land on the exact frame the reviewer flagged instead of blowing
  // past it. Callers who want to seek WITHOUT pausing (e.g. background jumps)
  // would need a separate helper; today every seek is comment-driven.
  const seek = useCallback((s: number) => {
    playerRef.current?.seek(s);
    playerRef.current?.pause();
  }, []);
  const getCurrentTime = useCallback(() => playerRef.current?.getCurrentTime() ?? 0, []);

  // Selection is bidirectional: timeline dot click and comment card click both
  // route through here. Card click also seeks the video (comment list still
  // owns that side effect since it knows the comment's timestampSeconds).
  const handleSelectComment = useCallback((id: string) => {
    setSelectedCommentId(id);
    // Timeline dot click routes here BEFORE seek (video-player also calls
    // onSeek on marker click). Pause here too so the timeline path doesn't
    // depend on the seek() wrapper above.
    playerRef.current?.pause();
  }, []);

  const activeVersion = useMemo(() => {
    if (!selectedVersionId) return currentVersion;
    return versions.find((v) => v.id === selectedVersionId) ?? currentVersion;
  }, [selectedVersionId, versions, currentVersion]);

  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith("/admin/watch/");
  const workspaceMode = mode === "workspace";

  // Per-version comments (Frame.io parity). In workspace mode we fetch the
  // active version's thread and refetch on version switch. Guest mode keeps the
  // comments passed in as a prop (its own path is already per-version).
  const workspaceComments = useWatchComments(
    "workspace",
    workspaceMode ? activeVersion?.id : undefined,
  );
  const effectiveComments = workspaceMode ? (workspaceComments.data ?? []) : comments;

  // Same two-panel gap pattern as AppShell — thin `gap-px` seam between the
  // top bar and the body, and another seam between the video and the right
  // panel. Each panel is `rounded-tl-2xl rounded-bl-2xl` so the LEFT side that
  // touches the sidebar / seam gets an S-curve. Right-side of top-bar/body
  // also gets top/bottom-right rounding so the seam looks curved on both ends.
  const topBarWrap = "overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-background";
  // Mobile: fixed 45vh so the video area is predictable and comments have room
  // below. Desktop: flex-1 to fill the row (unchanged).
  const videoWrap = "flex h-[45vh] overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-black md:h-auto md:flex-1";
  // Mobile: full-width panel taking remaining vertical space. Desktop: fixed
  // side column at w-80 (or lg:w-96), same as before.
  const rightPanelWrap = "flex min-h-0 flex-1 flex-col overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-2xl rounded-br-2xl bg-background md:w-80 md:flex-none lg:w-96";

  return (
    // h-[100dvh] over h-screen — dynamic viewport height accounts for mobile
    // browser chrome (address bar collapse/expand on iOS Safari). Falls back
    // to 100vh on unsupported browsers.
    <div className="flex h-screen h-[100dvh] bg-sidebar text-foreground">
      {/* Desktop rail — hidden on mobile. Guests never see this to begin with. */}
      {workspaceMode && (
        <div className="hidden md:contents">
          <WatchRail isAdmin={isAdminRoute} />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <div className={topBarWrap}>
          {workspaceMode ? (
            <WorkspaceTopBar
              backHref={backHref}
              title={title}
              versions={versions}
              activeVersionId={activeVersion?.id ?? null}
              onSelectVersion={setSelectedVersionId}
              rootVideoId={rootVideoId ?? null}
              rootShareToken={rootShareToken ?? null}
              rootDownloadAllowed={rootDownloadAllowed ?? false}
              panelOpen={panelOpen}
              onTogglePanel={() => setPanelOpen((v) => !v)}
              isAdminRoute={isAdminRoute}
            />
          ) : (
            <GuestTopBar
              adminName={adminName}
              title={title}
              versions={versions}
              activeVersionId={activeVersion?.id ?? null}
              onSelectVersion={setSelectedVersionId}
              shareToken={shareToken}
              currentVideoId={activeVersion?.id ?? null}
              downloadAllowed={!!downloadAllowed}
              panelOpen={panelOpen}
              onTogglePanel={() => setPanelOpen((v) => !v)}
            />
          )}
        </div>

        {/* Mobile: stack video over panel (flex-col). Desktop: side-by-side. */}
        <div className="flex min-h-0 flex-1 flex-col gap-px md:flex-row">
          <div className={videoWrap}>
            <MediaSurface
              ref={playerRef}
              assetType={activeVersion?.assetType}
              src={activeVersion?.cdnUrl ?? null}
              comments={effectiveComments}
              mode={mode}
              watchKey={watchKey}
              currentVersionId={activeVersion?.id ?? null}
              selectedCommentId={selectedCommentId}
              onSelectComment={handleSelectComment}
            />
          </div>

          {panelOpen && (
            <aside className={rightPanelWrap}>
              <RightPanel
                mode={mode}
                currentVersion={activeVersion}
                comments={effectiveComments}
                watchKey={watchKey}
                onSeek={seek}
                getCurrentTime={getCurrentTime}
                selectedCommentId={selectedCommentId}
                onSelectComment={handleSelectComment}
                assetType={activeVersion?.assetType}
              />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function WatchError({ message, backHref }: { message: string; backHref: string | null }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6 text-center">
      <div className="max-w-md space-y-3">
        <div className="text-lg font-semibold text-foreground">Something went wrong</div>
        <div className="text-sm text-muted-foreground">{message}</div>
        {backHref && (
          <Link to={backHref} className="inline-block text-sm text-primary hover:underline">
            Go back
          </Link>
        )}
      </div>
    </div>
  );
}

function pickLatest(versions: WatchVersion[]): WatchVersion | null {
  if (!versions?.length) return null;
  return versions.reduce((best, v) => (v.versionNumber > best.versionNumber ? v : best), versions[0]);
}

// ── Loading / error states ───────────────────────────────────────────────────

function WatchLoading() {
  return (
    <div className="grid min-h-[400px] flex-1 place-items-center">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );
}

// ── Sidebar rail (workspace mode only) ───────────────────────────────────────

// Kept in exact parity with the corresponding layout files:
//   src/layouts/app-layout.tsx (APP_NAV)
//   src/layouts/admin-layout.tsx (ADMIN_NAV)
// Any change to those files should be mirrored here.
const APP_NAV: RailNavItem[] = [
  { kind: "link", to: "/app/projects", label: "Projects", icon: FolderKanban },
  { kind: "link", to: "/app/upload", label: "Upload", icon: UploadCloud },
  { kind: "link", to: "/app/trash", label: "Trash", icon: Trash2 },
  { kind: "custom", key: "notifications", render: () => <NotificationBell to="/app/notifications" /> },
  { kind: "link", to: "/app/settings", label: "Settings", icon: Settings },
];

const ADMIN_NAV: RailNavItem[] = [
  // Search navigates to the search page — same reason as admin-layout: focus
  // on a hidden input was a no-op on mobile.
  { kind: "link", to: "/admin/search", label: "Search", icon: Search },
  { kind: "link", to: "/admin/trash", label: "Trash", icon: Trash2 },
  { kind: "custom", key: "notifications", render: () => <NotificationBell to="/admin/notifications" /> },
  { kind: "link", to: "/admin/settings", label: "Workspace settings", icon: Settings },
];

function WatchRail({ isAdmin }: { isAdmin: boolean }) {
  const { user, role, workspaceName } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <SidebarRail
      brandTo={isAdmin ? "/admin/projects" : "/app/projects"}
      primaryNav={isAdmin ? ADMIN_NAV : APP_NAV}
      userMenu={
        <UserMenu
          name={user?.name || user?.email || "Account"}
          email={user?.email}
          roleLabel={roleDisplayName(role)}
          workspaceName={workspaceName ?? undefined}
          settingsTo={isAdmin ? "/admin/settings" : "/app/settings"}
          isLoggingOut={logout.isPending}
          onLogout={() =>
            logout.mutate(undefined, { onSuccess: () => navigate("/login", { replace: true }) })
          }
        />
      }
    />
  );
}

// ── Workspace top bar ───────────────────────────────────────────────────────

function WorkspaceTopBar({
  backHref,
  title,
  versions,
  activeVersionId,
  onSelectVersion,
  rootVideoId,
  rootShareToken,
  rootDownloadAllowed,
  panelOpen,
  onTogglePanel,
  isAdminRoute,
}: {
  backHref: string | null;
  title: string;
  versions: WatchVersion[];
  activeVersionId: string | null;
  onSelectVersion: (id: string | null) => void;
  rootVideoId: string | null;
  rootShareToken: string | null;
  rootDownloadAllowed: boolean;
  panelOpen: boolean;
  onTogglePanel: () => void;
  isAdminRoute: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const projectsBasePath = isAdminRoute ? "/admin/projects" : "/app/projects";
  // Return to the previous page. On a direct load / refresh there's no in-app
  // history (location.key === "default"), so fall back to backHref.
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else if (backHref) navigate(backHref);
  };
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
      {backHref && (
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          title="Back"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
        </button>
      )}

      <VersionSwitcher
        title={title}
        versions={versions}
        activeVersionId={activeVersionId}
        onSelect={onSelectVersion}
      />

      <ProjectSearchInline projectsBasePath={projectsBasePath} />

      <ShareButton
        rootVideoId={rootVideoId}
        rootShareToken={rootShareToken}
        rootDownloadAllowed={rootDownloadAllowed}
        title={title}
      />

      <PanelToggle open={panelOpen} onToggle={onTogglePanel} />
    </header>
  );
}

function ShareButton({
  rootVideoId,
  rootShareToken: _rootShareToken,
  rootDownloadAllowed,
  title,
}: {
  rootVideoId: string | null;
  /** Legacy per-video share token — kept in the prop chain for backward
   * compatibility with the WorkspaceTopBar contract but ignored now that
   * the ShareDialog mints first-class /shares rows on demand (Task 14). */
  rootShareToken: string | null;
  rootDownloadAllowed: boolean;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  if (!rootVideoId) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Share"
        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
      >
        <Share2 className="h-3.5 w-3.5" />
        Share
      </button>
      <ShareDialog
        open={open}
        onOpenChange={setOpen}
        items={[{ type: "video", id: rootVideoId, name: title }]}
        defaults={{ downloadAllowed: rootDownloadAllowed }}
      />
    </>
  );
}

function ProjectSearchInline({ projectsBasePath }: { projectsBasePath: string }) {
  const navigate = useNavigate();
  const { data: projects } = useProjects(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const results =
    query.trim()
      ? (projects ?? [])
          .filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()))
          .slice(0, 6)
      : [];

  return (
    <div ref={containerRef} className="relative hidden max-w-xs flex-1 md:block">
      <div className="flex h-8 w-full items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 text-sm transition-colors focus-within:border-ring focus-within:bg-background">
        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <input
          id="workspace-search-input"
          type="search"
          placeholder="Search projects…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() =>
            window.setTimeout(() => {
              if (!containerRef.current?.contains(document.activeElement)) setOpen(false);
            }, 100)
          }
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key === "Enter" && results.length > 0) {
              navigate(`${projectsBasePath}/${results[0].id}`);
              setQuery("");
              setOpen(false);
            }
          }}
          className="w-full bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
      </div>
      {open && query.trim() && (
        <div className="absolute left-0 top-full z-40 mt-2 w-full overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-card">
          {results.length === 0 ? (
            <p className="px-2 py-2 text-xs text-muted-foreground">No projects match "{query}".</p>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  navigate(`${projectsBasePath}/${p.id}`);
                  setQuery("");
                  setOpen(false);
                }}
                className="flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-secondary"
              >
                {p.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Guest top bar ───────────────────────────────────────────────────────────

function GuestTopBar({
  adminName,
  title,
  versions,
  activeVersionId,
  onSelectVersion,
  shareToken,
  currentVideoId,
  downloadAllowed,
  panelOpen,
  onTogglePanel,
}: {
  adminName?: string;
  title: string;
  versions: WatchVersion[];
  activeVersionId: string | null;
  onSelectVersion: (id: string | null) => void;
  shareToken?: string;
  currentVideoId: string | null;
  downloadAllowed: boolean;
  panelOpen: boolean;
  onTogglePanel: () => void;
}) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/20 text-xs font-semibold uppercase text-primary">
        {adminName ? initialsFrom(adminName) : "?"}
      </div>
      <div className="hidden text-sm font-medium text-foreground sm:block">
        {adminName ? `${adminName}'s Workspace` : "Shared with you"}
      </div>
      <span className="hidden text-muted-foreground sm:block">/</span>

      <VersionSwitcher
        title={title}
        versions={versions}
        activeVersionId={activeVersionId}
        onSelect={onSelectVersion}
      />

      {downloadAllowed && shareToken && currentVideoId && (
        <a
          href={`/api/watch/${shareToken}/download?videoId=${currentVideoId}`}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          title="Download this version"
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </a>
      )}

      <PanelToggle open={panelOpen} onToggle={onTogglePanel} />
    </header>
  );
}

function PanelToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
      aria-label={open ? "Hide comments panel" : "Show comments panel"}
      title={open ? "Hide comments panel" : "Show comments panel"}
    >
      {open ? <PanelRightClose className="h-4 w-4" /> : <PanelRight className="h-4 w-4" />}
    </button>
  );
}

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Versions switcher (breadcrumb caret) ────────────────────────────────────

function VersionSwitcher({
  title,
  versions,
  activeVersionId,
  onSelect,
}: {
  title: string;
  versions: WatchVersion[];
  activeVersionId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const active = versions.find((v) => v.id === activeVersionId);
  const activeVersionNumber = active?.versionNumber;
  const hasStack = versions.length > 1;

  // Latest version by highest versionNumber — used to detect "user is on latest"
  // so the switcher can auto-return to "follow latest" when they pick it.
  const latestId = useMemo(() => {
    if (!versions.length) return null;
    return versions.reduce((best, v) => (v.versionNumber > best.versionNumber ? v : best), versions[0]).id;
  }, [versions]);

  // When only one version exists, render a plain title — no dropdown affordance.
  if (!hasStack) {
    return (
      <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{title}</div>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <DropdownMenu>
        <DropdownMenuTrigger
          className="flex max-w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60"
        >
          <span className="truncate">{title}</span>
          {activeVersionNumber != null && (
            <span className="shrink-0 text-xs text-muted-foreground">
              V{activeVersionNumber} of {versions.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          {[...versions]
            .sort((a, b) => b.versionNumber - a.versionNumber)
            .map((v) => {
              const isActive = v.id === activeVersionId;
              return (
                <DropdownMenuItem
                  key={v.id}
                  onSelect={() => onSelect(v.id === latestId ? null : v.id)}
                  className="flex-col items-start gap-0.5"
                >
                  <div className="flex w-full items-center gap-2">
                    {isActive ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <span className="inline-block h-3.5 w-3.5" />
                    )}
                    <span className="font-medium">V{v.versionNumber}</span>
                    {v.id === latestId && (
                      <span className="rounded bg-secondary/60 px-1 text-[10px] font-medium text-muted-foreground">
                        latest
                      </span>
                    )}
                  </div>
                  <div className="ml-5 text-[11px] text-muted-foreground">
                    {v.approvalStatus ? APPROVAL_STATUS_LABELS[v.approvalStatus] : "—"}
                    {v.createdAt && ` · ${formatShortDate(v.createdAt)}`}
                  </div>
                </DropdownMenuItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
